'use client';

import { type ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'next-i18next';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { Button, Input } from '@components/common/Forms';
import { TailwindSpinner } from '@components/common';
import { showBuyNeuroModal } from '@components/features/modals/buy-neuro/BuyNeuroModal';
import { BundleBenefits } from '@components/templates/employer/job-bundles/cards/BundleBenefits';
import { resolveJobAddV2BundleKey as resolveBundleKey } from '@components/templates/job-add-v2/utils/bundleType';
import { useCreditsCalculator } from '@hooks/credits/useCreditsCalculator';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { showVoiceAgentModalWithAuth } from '@utils/voice-agent/showVoiceAgentModalWithAuth';

interface HireServicesCardsProps {
  isAuth: boolean;
  bundlesData?: ApiSchema<'OfferBundle'>[];
}

type OfferBundleKey = NonNullable<ReturnType<typeof resolveBundleKey>>;
type QuantityByBundleKey = Partial<Record<OfferBundleKey, number>>;

const initialQuantityByBundleKey: QuantityByBundleKey = {
  hire: 1,
};

const getBundleKey = (bundle?: ApiSchema<'OfferBundle'>): OfferBundleKey | undefined =>
  resolveBundleKey(bundle as ApiSchema<'ActivationBundle'> | undefined);

const getRoundedPrice = (price?: number | null): number => Math.ceil(price ?? 0);

const getBundleActivationPrice = (bundle: ApiSchema<'OfferBundle'> | undefined): number => {
  const discountedPrice = getRoundedPrice(bundle?.price);
  const fullPrice = getRoundedPrice(bundle?.fullPrice);

  return fullPrice > 0 ? Math.max(fullPrice, discountedPrice) : discountedPrice;
};

type BundleTitleTranslator = (key: string) => string;

const getBundleDisplayTitle = (bundle: ApiSchema<'OfferBundle'> | undefined, t: BundleTitleTranslator): string => {
  const bundleKey = getBundleKey(bundle);

  if (bundleKey === 'hire') return t('job.bundle.name.hire500');
  if (bundleKey === 'plus' || bundleKey === 'pro') return bundle?.title || t('job.add.extra.promo.agent');

  return bundle?.title || t('job.add.extra.promo.agent');
};

const getBundleShortTitle = (bundle: ApiSchema<'OfferBundle'> | undefined, t: BundleTitleTranslator): string => {
  const bundleKey = getBundleKey(bundle);
  if (bundleKey === 'freemium') return t('standalone.freemium.bundle.name');

  const displayTitle = getBundleDisplayTitle(bundle, t);
  const parenthesizedTitle = displayTitle.match(/\(([^)]+)\)/)?.[1]?.trim();
  if (parenthesizedTitle) return parenthesizedTitle;

  if (bundleKey === 'hire') return 'Hire';
  if (bundleKey === 'pro') return 'Pro';
  if (bundleKey === 'plus') return 'Plus';
  if (bundleKey === 'standard') return 'Start';

  return displayTitle;
};

export const HireServicesCards = ({ isAuth, bundlesData }: HireServicesCardsProps) => {
  const { t } = useTranslation('common');
  const { router, locale } = useExtendedRouter();

  const handleSupportClick = () => {
    showVoiceAgentModalWithAuth(locale);
  };

  const [quantityByBundleKey, setQuantityByBundleKey] = useState<QuantityByBundleKey>(initialQuantityByBundleKey);
  const [creditAmount, setCreditAmount] = useState(0);
  const [hasTopUpBeenAdjusted, setHasTopUpBeenAdjusted] = useState(false);

  const bundleByKey = useMemo(
    () =>
      (bundlesData ?? []).reduce<Partial<Record<OfferBundleKey, ApiSchema<'OfferBundle'>>>>((acc, bundle) => {
        if (bundle?.parentBundleId) return acc;
        const bundleKey = getBundleKey(bundle);
        if (!bundleKey || acc[bundleKey]) return acc;
        acc[bundleKey] = bundle;
        return acc;
      }, {}),
    [bundlesData],
  );
  const plusBundle = bundleByKey.plus;
  const proBundle = bundleByKey.pro;
  const hireBundle = bundleByKey.hire;
  const standardBundle = bundleByKey.standard;

  // Selected bundle key. Defaults to Start — Freemium is offered as the
  // first pill option but the paid Start tier stays pre-selected so the
  // top-up calculator reflects a paid recruitment by default.
  const [currentBundleKey, setCurrentBundleKey] = useState<OfferBundleKey>('standard');
  const selectedBundle = bundleByKey[currentBundleKey] || standardBundle;
  const selectedTitle = getBundleShortTitle(selectedBundle, t);

  // Order the switcher pills Freemium → Start → Plus → Pro → Hire (ascending
  // price/complexity). Filter to bundles actually present in the response
  // so we don't render empty pills when the backend hides a tier.
  const bundleSwitcherKeys: OfferBundleKey[] = useMemo(
    () => (['freemium', 'standard', 'plus', 'pro', 'hire'] as OfferBundleKey[])
      .filter((key) => bundleByKey[key]),
    [bundleByKey],
  );

  const resolvedBundleId = selectedBundle?.id ?? '';
  const isActivateDisabled = !resolvedBundleId;
  const selectedBundleQuantityKey = getBundleKey(selectedBundle) ?? currentBundleKey ?? 'standard';

  const selectedUnitPrice = getBundleActivationPrice(selectedBundle);
  const quantity = quantityByBundleKey[selectedBundleQuantityKey] ?? 0;
  const initialTopUpAmount = useMemo(
    () => (
      getBundleActivationPrice(plusBundle) * (quantityByBundleKey.plus ?? 0)
      + getBundleActivationPrice(proBundle) * (quantityByBundleKey.pro ?? 0)
      + getBundleActivationPrice(hireBundle) * (quantityByBundleKey.hire ?? 0)
      + getBundleActivationPrice(standardBundle) * (quantityByBundleKey.standard ?? 0)
    ),
    [
      plusBundle?.price,
      plusBundle?.fullPrice,
      proBundle?.price,
      proBundle?.fullPrice,
      hireBundle?.price,
      hireBundle?.fullPrice,
      standardBundle?.price,
      standardBundle?.fullPrice,
      quantityByBundleKey.plus,
      quantityByBundleKey.pro,
      quantityByBundleKey.hire,
      quantityByBundleKey.standard,
    ],
  );

  useEffect(() => {
    if (hasTopUpBeenAdjusted) return;
    if (initialTopUpAmount <= 0) return;
    setCreditAmount(initialTopUpAmount);
  }, [hasTopUpBeenAdjusted, initialTopUpAmount]);

  const { priceData, priceIsValidating } = useCreditsCalculator(creditAmount);
  const discountLevels = priceData?.discountLevels ?? [];
  const nextTier = discountLevels.filter((level) => (level.minAmount ?? 0) > creditAmount).sort((a, b) => (a.minAmount ?? 0) - (b.minAmount ?? 0))[0];

  const differenceToNextTier = nextTier ? Math.round((nextTier.minAmount ?? 0) - creditAmount) : 0;
  const nextTierPercentage = nextTier?.percentage;
  const showNextTierSuggestion = nextTier && differenceToNextTier > 0;

  const updateBundleQuantity = (
    bundleKey: OfferBundleKey,
    bundle: ApiSchema<'OfferBundle'> | undefined,
    nextQuantity: number,
  ) => {
    const normalizedQuantity = Math.max(0, nextQuantity);
    const currentQuantity = quantityByBundleKey[bundleKey] ?? 0;
    const quantityDelta = normalizedQuantity - currentQuantity;
    if (quantityDelta === 0) return;

    setQuantityByBundleKey((prev) => ({
      ...prev,
      [bundleKey]: normalizedQuantity,
    }));
    setHasTopUpBeenAdjusted(true);
    setCreditAmount((prev) => Math.max(0, prev + quantityDelta * getBundleActivationPrice(bundle)));
  };

  const selectedJobBundleProjects = useMemo(() => ([
    {
      key: 'pro' as const,
      title: 'Pro',
      bundle: proBundle,
      quantity: quantityByBundleKey.pro ?? 0,
      total: getBundleActivationPrice(proBundle) * (quantityByBundleKey.pro ?? 0),
    },
    {
      key: 'hire' as const,
      title: 'Hire',
      bundle: hireBundle,
      quantity: quantityByBundleKey.hire ?? 0,
      total: getBundleActivationPrice(hireBundle) * (quantityByBundleKey.hire ?? 0),
    },
    {
      key: 'plus' as const,
      title: 'Plus',
      bundle: plusBundle,
      quantity: quantityByBundleKey.plus ?? 0,
      total: getBundleActivationPrice(plusBundle) * (quantityByBundleKey.plus ?? 0),
    },
    {
      key: 'standard' as const,
      title: 'Start',
      bundle: standardBundle,
      quantity: quantityByBundleKey.standard ?? 0,
      total: getBundleActivationPrice(standardBundle) * (quantityByBundleKey.standard ?? 0),
    },
  ].filter((item) => item.bundle && item.quantity > 0)), [
    proBundle,
    hireBundle,
    plusBundle,
    standardBundle,
    quantityByBundleKey.pro,
    quantityByBundleKey.hire,
    quantityByBundleKey.plus,
    quantityByBundleKey.standard,
  ]);

  const handleTopUp = () => {
    if (!isAuth) {
      void router.push(`/login?targetPath=${encodeURIComponent('/')}`, { withLocale: false });
      return;
    }
    showBuyNeuroModal(creditAmount);
  };

  const handleActivateBundle = () => {
    if (isActivateDisabled) return;
    if (!resolvedBundleId) {

      console.error('[HireServicesCards] missing bundle id for selection', { selectedBundle });
      return;
    }

    const targetPath = `/job-add/edit?preselected=${encodeURIComponent(resolvedBundleId)}`;
    if (!isAuth) {
      void router.push(`/login?targetPath=${encodeURIComponent(targetPath)}`, { withLocale: false });
      return;
    }
    void router.push(targetPath);
  };

  return (
    <div className="mt-4 w-full rounded-2xl bg-surface-100">
      <div className="grid grid-cols-1 gap-y-4 md:grid-cols-2 md:gap-x-8">
        <div id="bundle-selector" className="flex h-full min-h-0 min-w-0 flex-col rounded-2xl border bg-surface p-4 shadow-sm">
          <div className="text-left text-xs font-bold uppercase tracking-widest text-ink-medium">{t('mc.home_recruiter.configure_recruitments')}</div>

          <div className="mt-3 grid min-w-0 gap-5">
            {bundleSwitcherKeys.length > 1 && (
              <div id="bundle-switcher" className="flex flex-wrap gap-2">
                {bundleSwitcherKeys.map((key) => {
                  const isActive = key === currentBundleKey;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setCurrentBundleKey(key)}
                      aria-pressed={isActive}
                      className={`rounded-full border px-3 py-1 text-sm font-medium transition-colors ${isActive ? 'border-secondary bg-secondary text-white' : 'border-input/70 bg-surface-100 text-ink-medium hover:border-secondary/50 hover:text-ink'}`}
                    >
                      {getBundleShortTitle(bundleByKey[key], t)}
                    </button>
                  );
                })}
              </div>
            )}

            <div
              id="selected-job-bundle"
              className="rounded-xl border border-secondary/20 bg-secondary/5 p-3"
            >
              <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-6">
                <div className="flex flex-col text-left">
                  <span className="text-[36px] font-bold leading-none text-secondary">{selectedUnitPrice}</span>
                  <div className="mt-1 text-sm text-ink-medium">{t('employer.branding.price.desc')}</div>
                </div>
                <div id="selected-job-bundle-titles" className="flex min-w-0 flex-col text-left">
                  <div className="text-xl font-bold text-ink">{selectedTitle}</div>
                  {currentBundleKey === 'freemium' ? (
                    <BundleBenefits
                      benefits={[
                        { title: t('mc.services.bundle.freemium.mainbenefit') },
                        { title: t('mc.services.bundle.freemium.benefit') },
                      ]}
                      className="mt-2"
                    />
                  ) : (
                    !!selectedBundle?.benefits?.length && (
                      <BundleBenefits
                        benefits={selectedBundle.benefits}
                        className="mt-2"
                      />
                    )
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 text-left text-xs font-bold uppercase tracking-widest text-ink-medium">{t('mc.hone_recruiter.recruitment_number')}</div>
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              className="flex size-8 items-center justify-center rounded-lg border border-input bg-surface-100 text-lg text-ink-medium"
              onClick={() => {
                if (quantity <= 0) return;
                updateBundleQuantity(selectedBundleQuantityKey, selectedBundle, quantity - 1);
              }}
            >
              -
            </button>
            <span className="min-w-4 text-center text-base font-semibold text-ink">{quantity}</span>
            <button
              type="button"
              className="flex size-8 items-center justify-center rounded-lg border border-input bg-surface-100 text-lg text-ink-medium"
              onClick={() => {
                updateBundleQuantity(selectedBundleQuantityKey, selectedBundle, quantity + 1);
              }}
            >
              +
            </button>
          </div>

          <div className="mt-3 rounded-md bg-surface-100 p-3 text-left text-xs text-ink">
            <Trans i18nKey="mc.services.extra_services" />
          </div>

          <div className="mt-4 flex justify-center">
            <Button onClick={handleActivateBundle} rounding="full" color="secondary" size="sm" disabled={isActivateDisabled}>
              {t('offer.subscription_section.label.activate')}
            </Button>
          </div>
        </div>

        <div id="wallet-bar" className="flex h-full min-w-0 flex-col rounded-2xl border bg-surface p-4 shadow-sm">
          <div className="text-left text-xs font-bold uppercase tracking-widest text-ink-medium">{t('wallet.top-up.order')}</div>
          <div className="mt-3">
            <Input
              id="mcTopUpAmountInline"
              type="number"
              className="w-full"
              value={creditAmount || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setHasTopUpBeenAdjusted(true);
                const val = e.target.value;
                if (val === '' || val === null) {
                  setCreditAmount(0);
                  return;
                }
                const numVal = parseInt(val, 10);
                if (!Number.isNaN(numVal)) setCreditAmount(numVal);
              }}
            />

            {selectedJobBundleProjects.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {selectedJobBundleProjects.map((item) => (
                  <span
                    key={item.key}
                    className="inline-flex items-center gap-1.5 rounded-full bg-secondary/10 px-3 py-1 text-xs font-semibold text-secondary"
                  >
                    <span>
                      {t('mc.services.project_more')}
                      {' '}
                      {item.title}
                      {': '}
                      {item.quantity}
                      {' = '}
                      {item.total}
                    </span>
                    <button
                      type="button"
                      className="flex size-4 items-center justify-center rounded-full text-secondary hover:bg-secondary/15"
                      onClick={() => updateBundleQuantity(item.key, item.bundle, 0)}
                      aria-label={`${t('global.action.remove')} ${t('mc.services.project_more')} ${item.title}: ${item.quantity}`}
                    >
                      <XMarkIcon className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="mt-4 rounded-md bg-surface-100 p-3 text-sm">
            <div className="flex items-baseline justify-between py-1">
              <span>{t('page.offer.calculator.total.credits')}</span>
              <span>{priceIsValidating ? <TailwindSpinner className="size-4 text-ink-medium" /> : (priceData?.amount ?? 0)}</span>
            </div>
            <div className="flex items-baseline justify-between py-1">
              <span>{t('price.calculator.top-up.bonus_percentage')}</span>
              <span>{priceIsValidating ? <TailwindSpinner className="size-4 text-ink-medium" /> : `${priceData?.bonusPercentage ?? 0}%`}</span>
            </div>
            <div className="flex items-baseline justify-between py-1">
              <span>{t('price.calculator.top-up.bonus_amount')}</span>
              <span>{priceIsValidating ? <TailwindSpinner className="size-4 text-ink-medium" /> : (priceData?.bonusAmount ?? 0)}</span>
            </div>
            <div className="flex items-baseline justify-between py-1">
              <span>{t('price.calculator.top-up.next_bonus_level')}</span>
              <span className="text-right">
                {priceIsValidating ? (
                  <TailwindSpinner className="size-4 text-ink-medium" />
                ) : (
                  <>
                    {priceData?.nextBonusPercentage ?? 0}
                    {'% '}({priceData?.nextBonusAmount ?? 0}
                    {' €'})
                  </>
                )}
              </span>
            </div>
          </div>
          {showNextTierSuggestion && (
            <button
              type="button"
              onClick={() => {
                setHasTopUpBeenAdjusted(true);
                setCreditAmount(creditAmount + differenceToNextTier);
              }}
              className="mx-auto mt-2 flex w-fit items-center gap-2 rounded-md bg-secondary/10 px-2 py-1 text-xs font-medium text-secondary hover:bg-secondary/20"
            >
              <Trans
                i18nKey="page.offer-configurator.bonus_supplement.desc"
                values={{
                  nrOfCredits: differenceToNextTier,
                  nrPercentageBonus: nextTierPercentage,
                }}
              />
            </button>
          )}

          <div className="mt-1 grid grid-cols-2 items-baseline gap-2">
            <span className="text-left text-sm text-ink-medium">{t('page.offer.calculator.total_net.euro')}</span>
            <div className="text-right text-2xl font-bold text-ink">
              {priceIsValidating ? (
                <TailwindSpinner className="ml-auto size-6 text-ink-medium" />
              ) : (
                <>
                  <span>{priceData?.priceToPay ?? creditAmount}</span> <span className="text-sm font-medium">{t('employer.branding.price.desc')}</span>
                </>
              )}
            </div>
          </div>
          <span className="mt-1 block text-right text-xs text-ink-medium">* {t('euro_ron_fixed_exchange_rate')}</span>

          <div className="mt-6 text-center text-xs text-ink-medium">
            <span>{t('mc.hire.support.contact', 'For any questions or issues, contact support:')}</span>
            {' '}
            <Button
              type="button"
              styling="none"
              onClick={handleSupportClick}
              className="font-semibold text-primary hover:underline"
              data-test-id="mc-hire-support"
            >
              {t('global.support.phoneNr')}
            </Button>
          </div>

          <div className="mt-auto flex justify-center pt-4">
            <Button onClick={handleTopUp} rounding="full" color="secondary" size="sm">
              {t('global.buy-neuro')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
